import { useState, useCallback } from 'react';
import { Header } from './Header';
import { SearchForm } from './SearchForm';
import { ClientData } from './ClientData';
import { ProcessInfo } from './ProcessInfo';
import { Checklist } from './Checklist';
import { ExportSection } from './ExportSection';
import { Alert } from './Alert';
import { Footer } from './Footer';
import { Cliente, ProcessoInfo, DocumentoItem, ChecklistItem, TipoProcesso } from '../types';
import { DocumentFormatter } from '../utils/documentFormatter';
import { logger } from '../utils/logger';

export const MainApp: React.FC = () => {
  logger.debug('MainApp component rendering...');
  
  // Estados principais
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [processoInfo, setProcessoInfo] = useState<ProcessoInfo>({
    tipoProcesso: '' as TipoProcesso,
    nomeClienteApp: '',
    atividadeEnvio: '',
  });
  const [documentos, setDocumentos] = useState<DocumentoItem[]>([]);
  const [checklistGerado, setChecklistGerado] = useState<ChecklistItem[]>([]);
  
  // Estados de UI
  const [showClientData, setShowClientData] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string; isVisible: boolean }>({
    type: 'info',
    message: '',
    isVisible: false,
  });

  // Handlers
  const handleClienteEncontrado = useCallback((clienteEncontrado: Cliente) => {
    setCliente(clienteEncontrado);
    setShowClientData(true);
    setShowChecklist(true);
    setShowExport(false);
    setChecklistGerado([]);
    setDocumentos([]);
    showAlert('success', 'Cliente encontrado com sucesso!');
  }, []);

  const handleError = useCallback((message: string) => {
    showAlert('error', message);
  }, []);

  const handleProcessoChange = useCallback((novoProcesso: ProcessoInfo) => {
    setProcessoInfo(novoProcesso);
  }, []);

  const handleAddDocumento = useCallback(() => {
    const novoDocumento: DocumentoItem = {
      id: Date.now().toString(),
      tipo: 'Certidão de Nascimento',
      nomeTitular: '',
    };
    setDocumentos(prev => [...prev, novoDocumento]);
  }, []);

  const handleUpdateDocumento = useCallback((id: string, field: keyof DocumentoItem, value: string) => {
    setDocumentos(prev => 
      prev.map(doc => 
        doc.id === id ? { ...doc, [field]: value } : doc
      )
    );
  }, []);

  const handleRemoveDocumento = useCallback((id: string) => {
    setDocumentos(prev => prev.filter(doc => doc.id !== id));
  }, []);

  const handleGenerateChecklist = useCallback(() => {
    const itensChecklist: ChecklistItem[] = documentos
      .filter(doc => doc.nomeTitular.trim() !== '')
      .map(doc => ({
        tipo: doc.tipo,
        nomeTitular: doc.nomeTitular,
        texto: DocumentFormatter.formatarDocumento(doc.tipo, doc.nomeTitular),
      }));

    setChecklistGerado(itensChecklist);
    setShowExport(true);
    
    if (itensChecklist.length === 0) {
      showAlert('warning', 'Nenhum documento com nome do titular preenchido foi encontrado.');
    } else {
      showAlert('success', `Checklist gerado com ${itensChecklist.length} documento(s)!`);
    }
  }, [documentos]);

  const showAlert = useCallback((type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setAlert({ type, message, isVisible: true });
    setTimeout(() => {
      setAlert(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  }, []);

  const handleCloseAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  }, []);

  return (
    <div className="app">
      <Header />
      
      <main className="main">
        <div className="container">
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={handleCloseAlert}
            isVisible={alert.isVisible}
          />
          
          <SearchForm
            onClienteEncontrado={handleClienteEncontrado}
            onError={handleError}
          />
          
          {showClientData && cliente && (
            <ClientData cliente={cliente} />
          )}
          
          <ProcessInfo
            processoInfo={processoInfo}
            onProcessoChange={handleProcessoChange}
          />
          
          {showChecklist && (
            <Checklist
              itens={documentos}
              onAddItem={handleAddDocumento}
              onUpdateItem={handleUpdateDocumento}
              onRemoveItem={handleRemoveDocumento}
              onGenerateChecklist={handleGenerateChecklist}
              checklistGerado={checklistGerado}
            />
          )}
          
          <ExportSection
            cliente={cliente}
            processoInfo={processoInfo}
            checklistItems={checklistGerado}
            isVisible={showExport}
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};
